import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Loading } from "@/components/Common";

export const homeFor = (rol) => (rol === "tutor" ? "/portal" : rol === "docente" ? "/docente"
  : rol === "coordinadora" ? "/coordinacion" : "/admin");
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Academico from "@/pages/Academico";
import Actividades from "@/pages/Actividades";
import Finanzas from "@/pages/Finanzas";
import Documentos from "@/pages/Documentos";
import HorarioClases from "@/pages/HorarioClases";
import MenuEscolar from "@/pages/MenuEscolar";
import AdminReportes from "@/pages/admin/AdminReportes";
import AdminUsuarios from "@/pages/admin/AdminUsuarios";
import AdminMaterias from "@/pages/admin/AdminMaterias";
import AdminEstudiantes from "@/pages/admin/AdminEstudiantes";
import AdminFacturacion from "@/pages/admin/AdminFacturacion";
import AdminMensualidades from "@/pages/admin/AdminMensualidades";
import AdminDescuentos from "@/pages/admin/AdminDescuentos";
import AdminCatalogo from "@/pages/admin/AdminCatalogo";
import AdminAnuncios from "@/pages/admin/AdminAnuncios";
import AdminGastos from "@/pages/admin/AdminGastos";
import AdminCaja from "@/pages/admin/AdminCaja";
import AdminInventario from "@/pages/admin/AdminInventario";
import AdminNomina from "@/pages/admin/AdminNomina";
import AdminPagosAprobar from "@/pages/admin/AdminPagosAprobar";
import AdminSistema from "@/pages/admin/AdminSistema";
import AdminPlanes from "@/pages/admin/AdminPlanes";
import AdminRegistro from "@/pages/admin/AdminRegistro";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminConfiguracion from "@/pages/admin/AdminConfiguracion";
import AdminHorarios from "@/pages/admin/AdminHorarios";
import AdminECF from "@/pages/admin/AdminECF";
import DocenteHorario from "@/pages/docente/DocenteHorario";
import DocentePlanificacion from "@/pages/docente/DocentePlanificacion";
import CoordResumen from "@/pages/coordinacion/CoordResumen";
import CoordDocentes from "@/pages/coordinacion/CoordDocentes";
import CoordPlanificaciones from "@/pages/coordinacion/CoordPlanificaciones";
import CoordEstudiantes from "@/pages/coordinacion/CoordEstudiantes";
import CoordAlertas from "@/pages/coordinacion/CoordAlertas";
import CoordRegistroDocentes from "@/pages/coordinacion/CoordRegistroDocentes";
import RegistroTutor from "@/pages/RegistroTutor";
import RegistroDocente from "@/pages/RegistroDocente";
import GoogleCallback from "@/pages/GoogleCallback";
import DocenteMaterias from "@/pages/docente/DocenteMaterias";
import DocenteTareas from "@/pages/docente/DocenteTareas";
import DocenteClassroom from "@/pages/docente/DocenteClassroom";
import DocenteEstudiantes from "@/pages/docente/DocenteEstudiantes";
import DocentePonche from "@/pages/docente/DocentePonche";
import MisPagosNomina from "@/pages/MisPagosNomina";
import Manual from "@/pages/Manual";

const Guard = ({ children, roles, permiso }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12"><Loading label="Verificando sesión…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.rol)) return <Navigate to={homeFor(user.rol)} replace />;
  if (permiso && !(user.permisos || []).some((p) => permiso.includes(p))) {
    return <Navigate to={homeFor(user.rol)} replace />;
  }
  return children;
};

const Landing = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12"><Loading label="Cargando…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.rol)} replace />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/registro" element={<RegistroTutor />} />
            <Route path="/registro-docente" element={<RegistroDocente />} />
            <Route path="/auth/google" element={<GoogleCallback />} />

            <Route path="/portal" element={<Guard><Layout /></Guard>}>
              <Route index element={<Dashboard />} />
              <Route path="academico" element={<Academico />} />
              <Route path="horario" element={<HorarioClases />} />
              <Route path="menu" element={<MenuEscolar />} />
              <Route path="actividades" element={<Actividades />} />
              <Route path="finanzas" element={<Finanzas />} />
              <Route path="documentos" element={<Documentos />} />
              <Route path="manual" element={<Manual />} />
            </Route>

            <Route path="/admin" element={<Guard roles={["superadmin", "admin", "secretaria"]}><Layout admin /></Guard>}>
              <Route index element={<AdminReportes />} />
              <Route path="usuarios" element={
                <Guard permiso={["usuarios:gestionar", "usuarios:familias"]}><AdminUsuarios /></Guard>} />
              <Route path="estudiantes" element={<AdminEstudiantes />} />
              <Route path="materias" element={<AdminMaterias />} />
              <Route path="facturacion" element={
                <Guard permiso={["facturas:crear", "facturas:cobrar"]}><AdminFacturacion /></Guard>} />
              <Route path="mensualidades" element={
                <Guard permiso={["facturas:crear"]}><AdminMensualidades /></Guard>} />
              <Route path="descuentos" element={
                <Guard permiso={["finanzas:descuentos_planes"]}><AdminDescuentos /></Guard>} />
              <Route path="catalogo" element={<AdminCatalogo />} />
              <Route path="anuncios" element={<AdminAnuncios />} />
              <Route path="gastos" element={
                <Guard permiso={["finanzas:gastos_ventas"]}><AdminGastos /></Guard>} />
              <Route path="caja" element={<Guard permiso={["finanzas:reportes"]}><AdminCaja /></Guard>} />
              <Route path="inventario" element={
                <Guard permiso={["inventario:gestionar"]}><AdminInventario /></Guard>} />
              <Route path="nomina" element={
                <Guard permiso={["nomina:gestionar"]}><AdminNomina /></Guard>} />
              <Route path="pagos-aprobar" element={
                <Guard permiso={["facturas:cobrar"]}><AdminPagosAprobar /></Guard>} />
              <Route path="sistema" element={
                <Guard permiso={["sistema:respaldo"]}><AdminSistema /></Guard>} />
              <Route path="planes" element={
                <Guard permiso={["finanzas:descuentos_planes"]}><AdminPlanes /></Guard>} />
              <Route path="registro" element={
                <Guard permiso={["registro:solicitudes"]}><AdminRegistro /></Guard>} />
              <Route path="menu" element={<Guard permiso={["comunicacion:menu"]}><AdminMenu /></Guard>} />
              <Route path="configuracion" element={
                <Guard permiso={["configuracion:gestionar"]}><AdminConfiguracion /></Guard>} />
              <Route path="horarios" element={<AdminHorarios />} />
              <Route path="alertas" element={<CoordAlertas />} />
              <Route path="ecf" element={<Guard permiso={["ecf:gestionar"]}><AdminECF /></Guard>} />
              <Route path="mis-pagos" element={<MisPagosNomina />} />
              <Route path="manual" element={<Manual />} />
            </Route>

            <Route path="/docente" element={<Guard roles={["docente", "admin", "superadmin"]}><Layout docente /></Guard>}>
              <Route index element={<DocenteMaterias />} />
              <Route path="tareas" element={<DocenteTareas />} />
              <Route path="classroom" element={<DocenteClassroom />} />
              <Route path="horario" element={<DocenteHorario />} />
              <Route path="planificacion" element={<DocentePlanificacion />} />
              <Route path="estudiantes" element={<DocenteEstudiantes />} />
              <Route path="ponche" element={<DocentePonche />} />
              <Route path="mis-pagos" element={<MisPagosNomina />} />
              <Route path="manual" element={<Manual />} />
            </Route>

            <Route path="/coordinacion" element={
              <Guard roles={["coordinadora", "admin", "superadmin"]}><Layout coord /></Guard>}>
              <Route index element={<CoordResumen />} />
              <Route path="docentes" element={<CoordDocentes />} />
              <Route path="planificaciones" element={<CoordPlanificaciones />} />
              <Route path="horarios" element={<AdminHorarios />} />
              <Route path="tareas" element={<DocenteTareas />} />
              <Route path="estudiantes" element={<CoordEstudiantes />} />
              <Route path="alertas" element={<CoordAlertas />} />
              <Route path="materias" element={<AdminMaterias />} />
              <Route path="registro-docentes" element={<CoordRegistroDocentes />} />
              <Route path="mis-pagos" element={<MisPagosNomina />} />
              <Route path="manual" element={<Manual />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
